import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { createSelector, unwrapResult } from '@reduxjs/toolkit'
import {
  CheckoutAppSelector,
  useAppSelector,
  useAppDispatch,
  addOAuthAuthorizationErrorIntoCallbackUrlQueryArgs,
  fetchOAuthCode,
  addOAuthAuthorizationCodeIntoCallbackUrlQueryArgs,
} from '../'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { OAuthAuthorizationFlow, OAuthError, OAuthParams, OAuthState } from '../../model'
import { oAuthErrorQueryParamMapper, shouldAttemptAccountLink, toOAuthScopeArray } from '../../utils/oAuth'

/* Selectors */

export const oAuthStateSelector: CheckoutAppSelector<OAuthState> = (state) => state.oAuth
export const oAuthParamsSelector: CheckoutAppSelector<OAuthParams> = createSelector(oAuthStateSelector, (oAuth) => ({
  clientId: oAuth.clientId,
  responseType: oAuth.responseType,
  redirectUri: oAuth.redirectUri,
  scope: oAuth.scope,
  state: oAuth.state,
}))

/* React Hooks */

export const useOAuthStateSelector = (): OAuthState => useAppSelector(oAuthStateSelector)

export const useOAuthAuthorizationFlow = (): OAuthAuthorizationFlow => {
  const oAuthState = useOAuthStateSelector()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()

  const handleOAuthAuthorizationError = async (error: OAuthError, state: string): Promise<void> => {
    const errorQueryParam = oAuthErrorQueryParamMapper(error)
    await dispatch(
      addOAuthAuthorizationErrorIntoCallbackUrlQueryArgs({
        error: errorQueryParam,
        state,
      })
    )
  }

  const doOAuthAuthorization = async (): Promise<void> => {
    if (shouldAttemptAccountLink(oAuthState) && valueIsNotNullOrUndefined(oAuthState.error)) {
      // @ts-expect-error: OPERATION BLEED STOPPER
      handleOAuthAuthorizationError(oAuthState.error, oAuthState.state)
    } else if (shouldAttemptAccountLink(oAuthState) && !valueIsNotNullOrUndefined(oAuthState.error)) {
      const { clientId, responseType, scope, redirectUri } = oAuthState
      // fetch auth code
      try {
        const { code } = unwrapResult(
          await dispatch(
            fetchOAuthCode({
              clientId,
              scopes: toOAuthScopeArray(scope),
              grantType: responseType,
              redirectUri,
            })
          )
        )
        // add auth code and state into callback url
        await dispatch(
          addOAuthAuthorizationCodeIntoCallbackUrlQueryArgs({
            code,
            state: oAuthState.state,
          })
        )
        logEvent('Checkout OAuth Authorization Succeeded')
      } catch (e) {
        // @ts-expect-error: OPERATION BLEED STOPPER
        handleOAuthAuthorizationError(e, oAuthState.state)
        logEvent('Checkout OAuth Authorization failed')
      }
    }
  }

  const oAuthAuthorization = async (isTrusted: boolean): Promise<void> => {
    if (!isTrusted) {
      await doOAuthAuthorization()
    }
  }

  return {
    oAuthAuthorization,
  }
}
